import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteProductReviewImage, fetchProductReviewImagesList } from "../../shared/productChildMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function ProductReviewImagesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const iRes = await fetchProductReviewImagesList();
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/product-review-images?edit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this review image?"))) return;
    const res = await deleteProductReviewImage(id);
    if (!res.ok) setError(res.error ?? "Failed to delete review image");
    else await load();
  };

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item,
        review: `Review #${item.productReviewId}`,
        image: item.image ?? "-"
      })),
    [items]
  );

  const total = rows.length;
  const columns = [
      { field: "review", headerName: "Review", flex: 1, minWidth: 140 },
      { field: "image", headerName: "Image", flex: 1, minWidth: 180 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Product Review Images</h2>
          <p>{loading ? "Loading review images..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/product-review-images" className="icon-button icon-button--toolbar" title="Add review image" aria-label="Add review image">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
